import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col, ListGroup, Form, Button, Card } from 'react-bootstrap';
import { FaTrash, FaPlus, FaMinus } from 'react-icons/fa';
import Message from '../components/Message';
import Loader from '../components/Loader';
import { addToCart, removeFromCart, clearCartItems } from '../slices/cartSlice';
import { useCreateSupplyMutation, useUploadSupplyImagesMutation } from '../slices/suppliesApiSlice';
import { useGetSuppliersQuery } from '../slices/suppliersApiSlice';
import { toast } from 'react-toastify';

const SupplysForm = () => {
  
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const cart = useSelector((state) => state.cart);
  const { cartItems } = cart;
  
  const [uploadSupplyImages] = useUploadSupplyImagesMutation();
  
  const [images, setImages] = useState([]);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [note, setNote] = useState('');
  const [status, setStatus] = useState('');
  const [supplyDate, setSupplyDate] = useState(new Date().toISOString().split('T')[0]);
  const [expiryDate, setExpiryDate] = useState(new Date().toISOString().split('T')[0]);
  const [costs, setCosts] = useState({}); // Track individual costs for each item
  const [totalCost, setTotalCost] = useState(0); // Track total cost
  
  const { data: suppliersData, isLoading, error } = useGetSuppliersQuery({ keyword: supplierSearch });
  const [createSupply, { isLoading: savingSupplys, error: suppliesError }] = useCreateSupplyMutation();

  useEffect(() => {
    if (!cartItems.length) return; // Prevent unnecessary calculations if cart is empty
  
    const initialCosts = cartItems.reduce((acc, item) => {
      acc[item._id] = Math.round(item.qty * item.price * 0.45);
      return acc;
    }, {});
  
    setCosts(initialCosts);
  
    setTotalCost(Object.values(initialCosts).reduce((acc, cost) => acc + cost, 0));
  }, [cartItems]);
  
  
  useEffect(() => {
    if (suppliersData?.suppliers) {
      const filtered = suppliersData.suppliers.filter((supplier) =>
        supplier.name.toLowerCase().includes(supplierSearch.toLowerCase())
      );
      setFilteredSuppliers(filtered);
    }
  }, [supplierSearch, suppliersData]);

  const handleSupplierSelect = (supplier) => {
    setSelectedSupplier(supplier);
    setSupplierSearch(supplier.name);
    setFilteredSuppliers([]);
  };

  const adjustCostHandler = (id, adjustment) => {
    const updatedCosts = { ...costs };
    updatedCosts[id] = Math.max(0, updatedCosts[id] + adjustment); // Prevent cost from going below 0
    setCosts(updatedCosts);

    // Update total cost
    const newTotalCost = Object.values(updatedCosts).reduce((acc, cost) => acc + cost, 0);
    setTotalCost(newTotalCost);
  };

  const addToCartHandler = (product, qty) => {
    dispatch(addToCart({ ...product, qty }));
  };

  const removeFromCartHandler = (id) => {
    dispatch(removeFromCart(id));
    // Update costs and total cost
    const updatedCosts = { ...costs };
    delete updatedCosts[id];
    setCosts(updatedCosts);
    const newTotalCost = Object.values(updatedCosts).reduce((acc, cost) => acc + cost, 0);
    setTotalCost(newTotalCost);
  };

  const placeSupplyHandler = async () => {
    try {
      if (!selectedSupplier) {
        toast.error('Please select a supplier');
        return;
      }

      if (cartItems.length === 0) {
        toast.error('Your cart is empty');
        return;
      }

      const suppliesData = {
        supplyItems: cartItems.map((item) => ({
          name: item.name,
          qty: item.qty,
          price: item.price,
          itemCost: costs[item._id], // Add the cost of each item
          images: item.images[0],
          product: item._id,
        })),
        supplierDetails: selectedSupplier._id,
        note,
        status,
        date: supplyDate,
        expiryDate,
        images,
        
      };

      const response = await createSupply(suppliesData).unwrap();
      console.log('Response:', response);
      dispatch(clearCartItems());
      toast.success('Supply successfully recorded');
      navigate('/house/HomeScreen');
    } catch (err) {
      console.error('Error:', err);
      toast.error(err?.data?.message || 'Failed to record supplies');
    }
  };
  // For each individual cart item:
const calculateItemProfit = (totalPrice, totalCost) => {
  return (totalPrice - totalCost).toFixed(2); // Profit = TotalPrice - TotalCost
};

const calculateItemUnitCost = (totalCost, qty) => {
  return qty > 0 ? (totalCost / qty).toFixed(2) : 0; // UnitCost = TotalCost / Quantity
};

// For the entire cart:
const totalQuantity = cartItems.reduce((acc, item) => acc + item.qty, 0); // Total Quantity
const totalPrice = cartItems.reduce((acc, item) => acc + item.qty * item.price, 0); // Total Price
const profit = (totalPrice - totalCost).toFixed(2); // Total Profit = TotalPrice - TotalCost
const unitCost = totalQuantity > 0 ? (totalCost / totalQuantity).toFixed(2) : 0; // Total UnitCost
//const cardCost = cartItems.reduce((acc, item) => acc + totalCost, 0);
const cardCost = (totalPrice - profit).toFixed(2);


const validateFiles = (files) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    for (let file of files) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`File type not allowed: ${file.type}`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`File size exceeds 5MB: ${file.name}`);
        return false;
      }
    }
    return true;
  };

const uploadImageHandler = async (files) => {
    if (!validateFiles(files)) return;

    const formData = new FormData();
    Array.from(files).forEach((file) => formData.append('images', file));

    try {
      const res = await uploadSupplyImages(formData).unwrap();
      toast.success(res.message);
      setImages([...images, ...res.images]);
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  const removeImageHandler = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };
return (
  <Row>
    <Col md={8}>
      <h1>Supplys Form</h1>
      {cartItems.length === 0 ? (
        <Message>
          Your cart is empty. <Button onClick={() => navigate('/house/HomeScreen')}>Go Back</Button>
        </Message>
      ) : (
        <ListGroup variant="flush">
          {cartItems.map((item) => (
            <ListGroup.Item key={item._id}>
              <Row>
                <Col md={2}>
                  {item.images?.length > 0 && (
                    <img
                      src={item.images[0]}
                      alt="Product Thumbnail"
                      style={{
                        width: '50px',
                        height: '50px',
                        objectFit: 'cover',
                      }}
                    />
                  )}
                </Col>
                <Col>{item.name}</Col>
                <Col md={2}>D{item.price}</Col>
                <Col md={2}>
                  <Form.Control
                    as="select"
                    value={item.qty}
                    onChange={(e) => addToCartHandler(item, Number(e.target.value))}
                  >
                    {[...Array(item.countInStock).keys()].map((x) => (
                      <option key={x + 1} value={x + 1}>
                        {x + 1}
                      </option>
                    ))}
                  </Form.Control>
                </Col>
                <Col md={2}>D{(item.qty * item.price).toFixed(2)}</Col>
                <Col md={1}>
                  <Button variant="light" onClick={() => removeFromCartHandler(item._id)}>
                    <FaTrash />
                  </Button>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col md={4}>Cost:</Col>
                <Col md={4}>D{costs[item._id]}</Col>
                <Col md={4}>
                  <Button variant="light" onClick={() => adjustCostHandler(item._id, -100)}>
                    <FaMinus />
                  </Button>
                  <Button variant="light" onClick={() => adjustCostHandler(item._id, -10)}>
                    <FaMinus />
                  </Button>
                  <Button variant="light" onClick={() => adjustCostHandler(item._id, -1)}>
                    <FaMinus />
                  </Button>
                  <Button variant="light" onClick={() => adjustCostHandler(item._id, 1)}>
                    <FaPlus />
                  </Button>
                  <Button variant="light" onClick={() => adjustCostHandler(item._id, 10)}>
                    <FaPlus />
                  </Button>
                  <Button variant="light" onClick={() => adjustCostHandler(item._id, 100)}>
                    <FaPlus />
                  </Button>
                </Col>
              </Row>
              <Row className="mt-2">
                <Col md={6}>
                  <strong>Profit:</strong> D
                  {calculateItemProfit(item.qty * item.price, costs[item._id])}
                </Col>
                <Col md={6}>
                  <strong>Unit Cost:</strong> D
                  {calculateItemUnitCost(costs[item._id], item.qty)}
                </Col>
              </Row>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </Col>
    <Col md={4}>
      <Card>
        <ListGroup variant="flush">
          <ListGroup.Item>
            <h2>Select Supplier</h2>
            {isLoading ? (
              <Loader />
            ) : error ? (
              <Message variant="danger">{error?.data?.message || 'Failed to load suppliers'}</Message>
            ) : (
              <>
                <Form.Group controlId="supplierSearch">
                  <Form.Label>Search Supplier</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Type supplier name"
                    value={supplierSearch}
                    onChange={(e) => setSupplierSearch(e.target.value)}
                  />
                </Form.Group>
                {filteredSuppliers.length > 0 && (
                  <ListGroup className="dropdown-menu mt-2">
                    {filteredSuppliers.map((supplier) => (
                      <ListGroup.Item
                        key={supplier._id}
                        action
                        onClick={() => handleSupplierSelect(supplier)}
                      >
                        {supplier.name} ({supplier.company})
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
                {selectedSupplier && (
                  <div className="mt-3">
                    <strong>Selected Supplier:</strong>
                    <p>
                      {selectedSupplier.name} ({selectedSupplier.company})<br />
                      Phone: {selectedSupplier.telephone}<br />
                      Address: {selectedSupplier.address}<br />
                      SERIAL: {selectedSupplier.serialNumber}
                    </p>
                    <Form.Group controlId="note">
                      <Form.Label>Note</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter additional notes"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                      />
                    </Form.Group>

                    <Form.Group controlId="status" className="mt-2">
                    <Form.Label>Status</Form.Label>
                    <Form.Control as="select" 
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    >
                     <option value="Select">select</option>
                     <option value="Credit">Credit</option>
                     <option value="Donation">Donation</option>
                     <option value="Cash">Cash</option>
                     </Form.Control>
                     </Form.Group>

                    <Form.Group controlId="supplyDate" className="mt-2">
                      <Form.Label>Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={supplyDate}
                        onChange={(e) => setSupplyDate(e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group controlId="expiryDate" className="mt-2">
                      <Form.Label>Expiry Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={expiryDate}
                        onChange={(e) => setExpiryDate(e.target.value)}
                      />
                    </Form.Group>
                    <Form.Group controlId="images" className="mb-3">
                                <Form.Label>Images</Form.Label>
                                <Form.Control
                                  type="file"
                                  multiple
                                  onChange={(e) => uploadImageHandler(e.target.files)}
                                />
                                <div className="mt-3">
                                  {images.map((img, idx) => (
                                    <div
                                      key={idx}
                                      style={{
                                        display: 'inline-block',
                                        position: 'relative',
                                        marginRight: '10px',
                                      }}
                                    >
                                      <img
                                        src={img}
                                        alt={`Uploaded ${idx}`}
                                        className="img-fluid"
                                        style={{ maxWidth: '100px', height: '100px', objectFit: 'cover' }}
                                      />
                                      <Button
                                        variant="danger"
                                        size="sm"
                                        onClick={() => removeImageHandler(idx)}
                                        style={{
                                          position: 'absolute',
                                          top: '0',
                                          right: '0',
                                          borderRadius: '50%',
                                          padding: '2px 5px',
                                        }}
                                      >
                                        ✖
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </Form.Group>

                  </div>
                )}
              </>
            )}
          </ListGroup.Item>
          <ListGroup.Item>
            <h3>Total Summary:</h3>
            <p><strong>Card Cost:</strong> D{cardCost}</p>
            <p style={{display: "none"}}><strong>Unit Cost:</strong> D{unitCost}</p>
            <p><strong>Profit:</strong> D{profit}</p>
            <p><strong>TotalRev:</strong> D{totalPrice}</p>
          </ListGroup.Item>
          {suppliesError && (
            <ListGroup.Item>
              <Message variant="danger">{suppliesError.message}</Message>
            </ListGroup.Item>
          )}
          <ListGroup.Item>
            <Button
              type="button"
              className="btn-block"
              disabled={cartItems.length === 0 || !selectedSupplier}
              onClick={placeSupplyHandler}
            >
              Submit Supplys
            </Button>
            {savingSupplys && <Loader />}
          </ListGroup.Item>
        </ListGroup>
      </Card>
    </Col>
  </Row>
);
};

export default SupplysForm;